@if (Vendor) {
  <div class="container-fluid mt-4 ps-4">
    <div class="row">
      <div class="col">
        <h2 class="mb-3">
          Admin{{ Vendor.legalName ? ' - ' + Vendor.legalName : '' }}
        </h2>
      </div>
    </div>

    <div class="row g-3 admin-grid">
      <!-- Left column: Fee Vouchers, Vendor Rank, Vendor Conversion -->
      <div class="col-12 col-xl-6">
        <div class="d-flex flex-column gap-3 h-100">
          @if (VendorId) {
            <app-display-card [panelHeight]="'fit'">
              <h3 class="text-center mb-2">Fee vouchers</h3>
              <p class="text-center text-secondary mb-3">
                Remaining:
                <span class="fw-semibold text-primary">{{
                  feeVouchersLeft
                }}</span>
              </p>
              <div class="inner-panel rounded admin-section">
                <p class="admin-explain">
                  Give a fee voucher to a vendor that needs to register and get
                  approved without being charged.
                </p>
                <p class="admin-explain">
                  Vouchers are limited — check with VIVE customer service for
                  details.
                </p>
                <p class="admin-explain mb-0">
                  To undo a voucher assignment, select Charge vendor and the
                  vendor will be required to pay the registration fee.
                </p>
                @if (
                  canManageFeeVouchers && (feeVouchersLeft > 0 || feeWaived)
                ) {
                  <div class="action-row justify-center mt-3">
                    <button
                      class="btn btn-primary"
                      (click)="addRemoveFeeWaiver()"
                    >
                      {{
                        feeWaived
                          ? 'Charge vendor'
                          : 'Add fee voucher to vendor'
                      }}
                    </button>
                  </div>
                }
              </div>
            </app-display-card>
          }

          @if ((ExemptVendorId || VendorId) && !ClientRegisteredVendorId) {
            <app-display-card [panelHeight]="'fit'">
              <h3 class="text-center mb-2">Vendor rank</h3>
              @if (CompanyServiceRanking) {
                <p class="text-center text-secondary mb-3">
                  Rank:
                  <span class="fw-semibold text-primary">{{
                    CompanyServiceRanking.rank
                  }}</span>
                </p>
              }
              <div class="inner-panel rounded admin-section">
                <div class="action-row justify-center">
                  <button
                    class="btn btn-primary"
                    (click)="assignVendorRanking()"
                  >
                    Assign vendor rank
                  </button>
                </div>
              </div>
            </app-display-card>
          }

          @if (
            canConvertClientRegisteredVendorToComplianceExempt ||
            canConvertExemptVendorToClientRegisteredVendor ||
            canConvertVendorToClientRegisteredVendor ||
            canConvertVendorToComplianceExempt
          ) {
            <app-display-card [panelHeight]="'fit'">
              <h3 class="text-center mb-2">Vendor conversion</h3>
              <div class="inner-panel rounded admin-section">
                <div class="d-flex flex-column gap-2">
                  @if (
                    (!VendorId &&
                      !ExemptVendorId &&
                      canConvertClientRegisteredVendorToComplianceExempt) ||
                    (canConvertVendorToComplianceExempt &&
                      !ClientRegisteredVendorId &&
                      !ExemptVendorId)
                  ) {
                    <button
                      class="btn btn-primary"
                      (click)="convertVendorToComplianceExempt()"
                    >
                      Convert vendor to compliance exempt
                    </button>
                  }
                  @if (
                    !ClientRegisteredVendorId &&
                    !VendorId &&
                    canConvertExemptVendorToClientRegisteredVendor
                  ) {
                    <button
                      class="btn btn-primary"
                      (click)="convertExemptVendorToClientRegistered()"
                    >
                      Convert vendor to
                      {{
                        Company.allowAutomatedBusinessVerificationClientRegisteredVendors
                          ? 'business verified'
                          : 'non-verified'
                      }}
                    </button>
                  }
                  @if (
                    canConvertVendorToClientRegisteredVendor &&
                    !ClientRegisteredVendorId &&
                    !ExemptVendorId
                  ) {
                    <button
                      class="btn btn-primary"
                      (click)="convertVendorToClientRegistered()"
                    >
                      Convert vendor to
                      {{
                        Company.allowAutomatedBusinessVerificationClientRegisteredVendors
                          ? 'business verified'
                          : 'non-verified'
                      }}
                    </button>
                  }
                </div>
              </div>
            </app-display-card>
          }
        </div>
      </div>

      <!-- Right column: Accounting Links, Force Active Status -->
      <div class="col-12 col-xl-6">
        <app-display-card [panelHeight]="'fit'">
          @if (isLinkableSystem) {
            <h3 class="text-center mb-3">Accounting links</h3>
            <div class="inner-panel rounded admin-section">
              <p class="admin-explain">
                Link this vendor to your accounting system below. After the link
                is made, VIVE will communicate with your AP system based on the
                configured settings.
              </p>
              <p class="admin-explain mb-3">
                For questions on accounting configurations, consult your company
                administrator or VIVE customer service.
              </p>

              <div class="d-flex flex-column gap-3">
                @for (apSystem of apSystemLinkingDataArr; track apSystem) {
                  @if (apSystem) {
                    <div
                      class="ap-system apSystem-{{
                        apSystem.config.apDisplayName ||
                          apSystem.config.apSystem
                      }}"
                    >
                      <div class="ap-system__head">
                        <span class="ap-system__name">{{
                          apSystem.config.apDisplayName ||
                            apSystem.config.apSystem
                        }}</span>
                        <span class="chip-host" appStatusStyleChanges>
                          {{ apSystem.ApVendor ? 'Linked' : 'Unlinked' }}
                        </span>
                      </div>
                      <dl class="ap-system__details">
                        @if (apSystem.ApVendor) {
                          <div class="ap-system__row">
                            <dt>Legal name</dt>
                            <dd>{{ apSystem.ApVendor.legalName }}</dd>
                          </div>
                          <div class="ap-system__row">
                            <dt>Accounting ID</dt>
                            <dd>{{ apSystem.ApVendor.vendorKey }}</dd>
                          </div>
                        }
                        <div class="ap-system__row">
                          <dt>Auto-linking</dt>
                          <dd>
                            {{
                              apSystem.CompanyVendor.allowEinAutoLink
                                ? 'Yes'
                                : 'No'
                            }}
                          </dd>
                        </div>
                      </dl>
                      <div class="action-row justify-end mt-2">
                        @if (apSystem.ApVendor && apSystem.config.isTwoWay) {
                          <button
                            class="btn btn-ghost"
                            (click)="syncNow(apSystem.ApVendor)"
                          >
                            Sync now
                          </button>
                        }
                        <button
                          class="btn btn-primary"
                          (click)="manageAccountingLinks(apSystem)"
                        >
                          Manage links
                        </button>
                      </div>
                    </div>
                  }
                }
              </div>
            </div>
          }

          @if (
            !CompanyVendor.forceTempActive &&
            allowForceActive() &&
            linkedAccounts
          ) {
            <h3 class="text-center mt-4 mb-3">Force active status</h3>
            <div class="inner-panel rounded admin-section">
              <p class="admin-explain mb-3">
                Force an active status for this vendor for a defined period of
                time using the dropdown below.
              </p>
              <form [formGroup]="ForceActiveFormGroup">
                <div class="form-group">
                  <label class="form-label" for="forceActiveDays">
                    Duration
                  </label>
                  <select
                    id="forceActiveDays"
                    class="form-select"
                    formControlName="days"
                    (change)="onForceActiveDaysChange()"
                  >
                    @for (
                      ForceActiveDayObject of ForceActiveDays;
                      track ForceActiveDayObject
                    ) {
                      <option [ngValue]="ForceActiveDayObject.value">
                        {{ ForceActiveDayObject.label }}
                      </option>
                    }
                  </select>
                </div>
                @if (ForceActiveFormGroup.value.days === 'custom') {
                  <div class="form-group">
                    <label class="form-label" for="customExpirationDate">
                      Custom expiration date
                    </label>
                    <input
                      id="customExpirationDate"
                      type="date"
                      class="form-control"
                      formControlName="customExpirationDate"
                      [min]="minExpirationDate"
                    />
                  </div>
                }
                <div class="action-row justify-end mt-3">
                  <button
                    type="button"
                    class="btn btn-primary"
                    (click)="setForceActiveDays()"
                  >
                    Set force active status
                  </button>
                </div>
              </form>
            </div>
          }

          @if (CompanyVendor.forceTempActive && linkedAccounts) {
            <div class="inner-panel rounded admin-section text-center">
              <p class="mb-0">
                Time remaining on force active status:
                <span class="fw-semibold">{{ getForceActiveTimeLeft() }}</span>
              </p>
            </div>
          }

          @if (canArchiveClientCreatedRelationship) {
            <h3 class="text-center mt-4 mb-3">Archive vendor</h3>
            <div class="inner-panel rounded admin-section">
              <p class="admin-explain mb-3">
                Archive this vendor to remove it from your vendor list. Any
                accounting system links are removed, and the vendor can no
                longer claim this profile. This cannot be undone.
              </p>
              <div class="action-row justify-end">
                <button
                  type="button"
                  class="btn btn-danger"
                  (click)="archiveClientCreatedRelationship()"
                >
                  Archive vendor
                </button>
              </div>
            </div>
          }
        </app-display-card>
      </div>
    </div>
  </div>
}
